import styles from './new-trip.module.css';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DestinationAndCalendarCom } from './inputForm/dateAndDes.jsx';
import { ClipLoader } from "react-spinners";
import { useTripInfo } from '../context';
import { API_URL } from '../utils/fatch.jsx';

export function NewTrip() {
    const [travelers, setTravelers] = useState(1);
    const [destination, setDestination] = useState('');
    const [country, setCountry] = useState(null);
    const [city, setCity] = useState(null);
    const [neighborhood, setNeighborhood] = useState(null);
    const [state, setState] = useState(null);

    const locationProps = {
        setDestination,
        setCountry,
        setCity,
        setNeighborhood,
        setState,
        destination,
    };

    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        },
    ]);
    const [waitingForNewTrip, setWaitingForNewTrip] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();
    const { setTrip_name, setCurrentTripId } = useTripInfo();

    const handleNewTripSubmit = async (e) => {
        e.preventDefault();
        setWaitingForNewTrip(true);
        setErrorMessage('');

        const newTripData = {
            name: e.target.elements['trip-name'].value,
            destination: destination,
            country,
            city,
            neighborhood,
            state,
            startDate: dateRange[0]?.startDate.toISOString().split('T')[0],
            endDate: dateRange[0]?.endDate.toISOString().split('T')[0],
            budget: parseFloat(e.target.elements['budget'].value),
            travelers: travelers,
        };

        try {
            const response = await fetch(`${API_URL}/newTrip`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newTripData)
            });

            if (!response.ok) {
                const data = await response.json();
                console.error(data);
                setErrorMessage(data.message || 'Failed to create new trip');
                return;
            }
            const data = await response.json();
            setCurrentTripId(data.currentTripId);
            setTrip_name(newTripData.name);
            e.target.reset();
            navigate('/add_expense');
        } catch (error) {
            console.error('Error adding new trip:', error);
            setErrorMessage('Something went wrong. Please try again.');
        } finally {
            setWaitingForNewTrip(false);
        }
    };

    return (
        <div className={styles.fullPageModal}>
            <Link to="/all_trips" className={styles.backBtn}>
                <i className="fa-solid fa-arrow-left"></i>
                Back
            </Link>

            <div className={styles.fullPageContent}>
                {/* ===== LEFT PANEL (desktop) ===== */}
                <aside className={styles.aside}>
                    <div className={styles.asideEmoji}>✈️</div>
                    <h2 className={styles.asideTitle}>Every great trip<br />starts with a plan</h2>
                    <p className={styles.asideText}>
                        Set it up once and RoamRite handles the rest of the journey with you.
                    </p>
                    <ul className={styles.asideList}>
                        <li><span className={styles.asideBullet}>💸</span> Track every expense against your budget</li>
                        <li><span className={styles.asideBullet}>🗺️</span> Find activities worth your time</li>
                        <li><span className={styles.asideBullet}>🎒</span> Let the AI pack your bag</li>
                        <li><span className={styles.asideBullet}>👥</span> Share it all with who you travel with</li>
                    </ul>
                </aside>

                {/* ===== RIGHT PANEL ===== */}
                <div className={styles.formPanel}>
                    {/* ===== HEADER ===== */}
                    <div className={styles.header}>
                        <span className={styles.mobileEmoji}>✈️</span>
                        <h3>
                            Plan Your <span className={styles.headerGradientText}>Next Adventure</span>
                        </h3>
                        <p className={styles.headerSubtext}>Fill in the details below to get started</p>
                    </div>

                {/* ===== FORM ===== */}
                <form className={styles.form} onSubmit={handleNewTripSubmit}>
                    {/* Trip Name */}
                    <div className={styles.inputGroup}>
                        <label className={styles.fieldLabel} htmlFor="new-trip-name">Trip name</label>
                        <input
                            type="text"
                            id="new-trip-name"
                            name="trip-name"
                            required
                            placeholder="Summer in Rome"
                            autoComplete="off"
                        />
                    </div>

                    {/* Destination + Calendar */}
                    <DestinationAndCalendarCom
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        type="create"
                        {...locationProps}
                    />

                    <div className={styles.sectionLabel}>Budget & Team</div>

                    <div className={styles.gridRow}>
                        {/* Budget */}
                        <div className={styles.inputGroup}>
                            <label className={styles.fieldLabel} htmlFor="new-trip-budget">Budget ($)</label>
                            <input
                                type="number"
                                id="new-trip-budget"
                                name="budget"
                                required
                                step="1"
                                min="1"
                                inputMode="numeric"
                                placeholder="2000"
                            />
                        </div>

                        {/* Travelers Stepper */}
                        <div className={styles.travelersGroup}>
                            <span className={`${styles.travelersLabel} ${travelers > 1 ? styles.travelersLabelActive : ''}`}>
                                Travelers
                            </span>
                            <div className={styles.travelersStepper}>
                                <button
                                    type="button"
                                    className={styles.stepperBtn}
                                    onClick={() => setTravelers(prev => Math.max(1, prev - 1))}
                                    disabled={travelers <= 1}
                                    aria-label="Decrease travelers"
                                >
                                    −
                                </button>
                                <div className={styles.stepperValue}>
                                    {travelers}
                                    <span className={styles.stepperValueSmall}>
                                        {travelers === 1 ? 'person' : 'people'}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className={styles.stepperBtn}
                                    onClick={() => setTravelers(prev => Math.min(50, prev + 1))}
                                    disabled={travelers >= 50}
                                    aria-label="Increase travelers"
                                >
                                    +
                                </button>
                            </div>
                            {/* Hidden input for form submission */}
                            <input
                                type="hidden"
                                name="travelers"
                                value={travelers}
                            />
                        </div>
                    </div>

                    {/* Error Message */}
                    {errorMessage && (
                        <div className={styles.errorMessage}>
                            <i className="fa-solid fa-circle-exclamation"></i>
                            {errorMessage}
                        </div>
                    )}

                    {/* Submit */}
                    <div className={styles.submitWrapper}>
                        <button
                            type="submit"
                            className={styles.submitBtn}
                            disabled={waitingForNewTrip}
                        >
                            <span className={styles.submitBtnContent}>
                                {waitingForNewTrip ? (
                                    <ClipLoader size={22} color={"#111827"} />
                                ) : (
                                    <>
                                        <span>Start Trip</span>
                                        <i className="fa-solid fa-arrow-right"></i>
                                    </>
                                )}
                            </span>
                        </button>
                    </div>
                </form>
                </div>
            </div>
        </div>
    );
}
